import memoize from 'lodash.memoize';
import validUrl from 'valid-url';
import { createSelector, createStructuredSelector } from 'reselect';
import * as technical from './iiif/technical';
import * as descriptive from './iiif/descriptive';
import * as linking from './iiif/linking';
import * as structural from './iiif/structural';
import { getAllExternalResources, getAllLayers, getAllServices, getAllCanvases, getAllImages } from '../all';

var sequence = memoize(function (selector) {
  /**************************************************
   * Technical properties
   *
   * - getId
   * - getType
   * - getViewingHint
   * - getViewingDirection
   **************************************************/
  var getId = createSelector(selector, technical.getId);
  var getType = createSelector(selector, technical.getType);
  var getViewingHint = createSelector(selector, function (sequenceEntity) {
    var viewingHint = technical.getViewingHint(sequenceEntity);
    switch (viewingHint) {
      case 'individuals':
      case 'paged':
      case 'continuous':
        return viewingHint;
      default:
        return validUrl.isWebUri(viewingHint) ? viewingHint : null;
    }
  });
  var getViewingDirection = createSelector(selector, technical.getViewingDirection);

  /**************************************************
   * Descriptive properties
   *
   * - getLabel
   * - getDescription
   * - getMetadata
   * - getAttribution
   * - getLogo
   * - getLicense
   * - getThumbnail
   **************************************************/
  var getLabel = createSelector(selector, descriptive.getLabel);

  var getDescription = createSelector(selector, descriptive.getDescription);

  var getMetadata = createSelector(selector, descriptive.getMetadata);

  var getAttribution = createSelector(selector, descriptive.getAttribution);

  var getLogo = createSelector(selector, descriptive.getLogo);

  var getLicense = createSelector(selector, descriptive.getLicense);

  var getThumbnailId = createSelector(selector, descriptive.getThumbnailId);
  var getThumbnail = createSelector(getThumbnailId, getAllImages, function (thumbnailId, allImages) {
    return allImages[thumbnailId] || thumbnailId;
  });

  /**************************************************
   * Linking properties
   *
   * - getSeeAlso
   * - getService
   * - getRelated
   * - getRendering
   * - getWithin
   * - getStartCanvas
   **************************************************/
  var getSeeAlsoIds = createSelector(selector, linking.getSeeAlso);
  var getSeeAlso = createSelector(getSeeAlsoIds, getAllExternalResources, function (seeAlsoIds, allExternalResources) {
    return seeAlsoIds.map(function (seeAlsoId) {
      return allExternalResources[seeAlsoId];
    });
  });

  var getServiceIds = createSelector(selector, linking.getService);
  var getService = createSelector(getServiceIds, getAllServices, function (serviceIds, allServices) {
    return serviceIds.map(function (serviceId) {
      return allServices[serviceId];
    });
  });

  var getRelatedIds = createSelector(selector, linking.getRelated);
  var getRelated = createSelector(getRelatedIds, getAllExternalResources, function (relatedIds, allExternalResources) {
    return relatedIds.map(function (relatedId) {
      return allExternalResources[relatedId];
    });
  });

  var getRenderingIds = createSelector(selector, linking.getRendering);
  var getRendering = createSelector(getRenderingIds, getAllExternalResources, function (renderingIds, allExternalResources) {
    return renderingIds.map(function (renderingId) {
      return allExternalResources[renderingId];
    });
  });

  var getWithinIds = createSelector(selector, linking.getWithin);
  var getWithin = createSelector(getWithinIds, getAllLayers, getAllExternalResources, function (withinIds, allLayers, allExternalResources) {
    return withinIds.map(function (within) {
      return allLayers[within.id] || allExternalResources[within.id];
    }).filter(function (e) {
      return e;
    });
  });

  var getStartCanvasId = createSelector(selector, linking.getStartCanvas);
  var getStartCanvas = createSelector(getStartCanvasId, getAllCanvases, function (canvasId, allCanvases) {
    return allCanvases[canvasId];
  });

  /**************************************************
   * Structural properties
   *
   * - getCanvases (Required)
   **************************************************/
  var getCanvasIds = createSelector(selector, structural.getCanvases);
  var getCanvases = createSelector(getCanvasIds, getAllCanvases, function (canvasIds, allCanvases) {
    return canvasIds.map(function (canvasId) {
      return allCanvases[canvasId];
    });
  });

  return {
    selector: selector,
    // Technical
    getId: getId,
    getType: getType,
    getViewingHint: getViewingHint,
    getViewingDirection: getViewingDirection,
    // Descriptive
    getLabel: getLabel,
    getDescription: getDescription,
    getMetadata: getMetadata,
    getAttribution: getAttribution,
    getLogo: getLogo,
    getLicense: getLicense,
    getThumbnailId: getThumbnailId,
    getThumbnail: getThumbnail,
    // Linking
    getSeeAlsoIds: getSeeAlsoIds,
    getSeeAlso: getSeeAlso,
    getServiceIds: getServiceIds,
    getService: getService,
    getRelatedIds: getRelatedIds,
    getRelated: getRelated,
    getRenderingIds: getRenderingIds,
    getRendering: getRendering,
    getWithinIds: getWithinIds,
    getWithin: getWithin,
    getStartCanvasId: getStartCanvasId,
    getStartCanvas: getStartCanvas,
    // Structural
    getCanvasIds: getCanvasIds,
    getCanvases: getCanvases
  };
});

export default sequence;